import { get, post, put, del } from './http';

export interface AttachmentItem {
  id?: string;
  fileObjectId?: string | number | null;
  name: string;
  size: string;
  url: string;
  downloadUrl?: string | null;
  downloadType: 'free' | 'timed_free' | 'lingshi' | 'vip';
  downloadTypes?: ('free' | 'timed_free' | 'lingshi' | 'vip')[];
  vipPrivilege?: boolean;
  lingshiCost?: number;
  timedFreeUntil?: string;
}

export interface LinkItem {
  label: string;
  url: string;
  type: 'github' | 'docs' | 'demo' | 'video' | 'other' | 'download';
}

export interface SolutionDto {
  id?: string;
  title: string;
  slug?: string;
  category: string;
  commercial: string;
  opensource: string;
  description: string;
  commercialDetail?: string | null;
  opensourceDetail?: string | null;
  costSaving: string;
  difficulty?: string;
  stars: number;
  tags?: string[];
  features?: string[];
  openSourceFeatures?: string[];
  costCompare?: {
    commercial: string;
    opensource: string;
  };
  deployDifficulty?: number;
  functionalScore?: {
    commercial: number;
    opensource: number;
  };
  image?: string;
  links?: LinkItem[];
  attachments?: AttachmentItem[];
  pinned?: boolean;
  offline?: boolean;
}

export interface AdminSolutionsResponse {
  total: number;
  list: SolutionDto[];
}

export interface PresignUploadResponse {
  uploadUrl: string;
  objectKey: string;
  bucket: string;
  endpoint: string;
}

export interface CompleteUploadResponse {
  fileObjectId: number;
  uploadedFileId: number;
  objectKey: string;
  displayName: string;
}

const PUBLIC_SOLUTIONS_CACHE_TTL = 5000;
const publicSolutionsInFlight = new Map<string, Promise<{ total: number; list: SolutionDto[] }>>();
const publicSolutionsCache = new Map<string, { cachedAt: number; data: { total: number; list: SolutionDto[] } }>();

// ── 方案管理接口 ─────────────────────────────────────────────────────────────

/**
 * 1. 查询方案管理列表
 */
export function getAdminSolutions(params: {
  search?: string;
  category?: string;
  page?: number;
  pageSize?: number;
}) {
  return get<AdminSolutionsResponse>('/admin/solutions', { params });
}

/**
 * 2. 创建新方案 (普通管理员返回带审核状态数据)
 */
export function createSolution(data: SolutionDto, resubmitReviewId?: number | string) {
  const url = resubmitReviewId ? `/admin/solutions?resubmitReviewId=${resubmitReviewId}` : '/admin/solutions';
  return post<{ solutionId?: number; reviewRequestId?: number; isPending?: boolean }>(
    url,
    data
  );
}

/**
 * 3. 更新方案
 */
export function updateSolution(id: string | number, data: SolutionDto, resubmitReviewId?: number | string) {
  const url = resubmitReviewId ? `/admin/solutions/${id}?resubmitReviewId=${resubmitReviewId}` : `/admin/solutions/${id}`;
  return put<{ solutionId?: number; reviewRequestId?: number; isPending?: boolean }>(
    url,
    data
  );
}

/**
 * 4. 删除方案
 */
export function deleteSolution(id: string | number) {
  return del<{ reviewRequestId?: number; isPending?: boolean }>(`/admin/solutions/${id}`);
}

/**
 * 5. 微调单个属性 (如置顶、上下架、修改排序权重)
 */
export function updateSolutionDirectFields(
  id: string | number,
  fields: {
    pinned?: boolean;
    offline?: boolean;
    sortOrder?: number;
    title?: string; // 传输标题用于普通管理员显示审批文本
  }
) {
  return post<{ reviewRequestId?: number; isPending?: boolean }>(
    `/admin/solutions/${id}/fields`,
    fields
  );
}

/**
 * 6. 批量操作 (批量删除/批量下架/批量上架)
 */
export function batchSolutionsAction(payload: {
  ids: (string | number)[];
  action: 'delete' | 'offline' | 'online';
}) {
  return post<{ reviewRequestId?: number; isPending?: boolean }>('/admin/solutions/batch', payload);
}

// ── OBS 直传接口 ─────────────────────────────────────────────────────────────

/**
 * 7. 申请预签名直传上传 URL
 */
export function presignUploadFile(payload: {
  fileName: string;
  contentType: string;
  fileCategory: 'avatar' | 'solution_attachment' | 'tool_attachment' | 'other';
}) {
  return post<PresignUploadResponse>('/files/presign-upload', payload);
}

/**
 * 8. 通知后端直传完成并备案落盘
 */
export function completeUploadFile(payload: {
  objectKey: string;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
  fileCategory: 'avatar' | 'solution_attachment' | 'tool_attachment' | 'other';
}) {
  return post<CompleteUploadResponse>('/files/complete', payload);
}

/**
 * 9. 公开查询方案列表 (免鉴权，前台用户与计数器使用)
 */
export function getPublicSolutions(params?: { search?: string; category?: string }) {
  const key = JSON.stringify(params || {});
  const now = Date.now();
  const cached = publicSolutionsCache.get(key);
  if (cached && now - cached.cachedAt < PUBLIC_SOLUTIONS_CACHE_TTL) {
    return Promise.resolve(cached.data);
  }

  const inFlight = publicSolutionsInFlight.get(key);
  if (inFlight) return inFlight;

  const req = get<{ total: number; list: SolutionDto[] }>('/public/solutions', { params })
    .then((res) => {
      publicSolutionsCache.set(key, { cachedAt: Date.now(), data: res });
      return res;
    })
    .finally(() => {
      publicSolutionsInFlight.delete(key);
    });

  publicSolutionsInFlight.set(key, req);
  return req;
}
